import { Component, Inject, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatCard } from '@angular/material/card';
import { CommonModule } from '@angular/common';
import { ModalPdfComponent } from '../../../generic/modal-pdf/modal-pdf.component';
import { HttpRequestService } from '../../../../utilities/http/request.utilities';
import { environment } from '../../../../../environments/environment';
import { ConfirmationDialogComponent } from '../../../generic/confirmation-dialog/confirmation-dialog.component';
import { lastValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';
import jsPDF from 'jspdf';
import { Store } from '@ngrx/store';
import { UserState } from '../../../../store/user/user.model';
import { MarkdownComponent } from 'ngx-markdown';

@Component({
  selector: 'app-view-transaction',
  standalone: true,
  imports: [MatIcon, FormsModule, MatCard, MatIcon, CommonModule, MarkdownComponent],
  templateUrl: './view-transaction.component.html',
  styleUrl: './view-transaction.component.css'
})
export class ViewTransactionComponent implements OnInit{
  showFeedbackForm = false;
  selectedReason: string = '';
  additionalFeedback: string = '';
  downloadURL: string | null = null;
  maxPrompts = environment.maxPrompts;
  @Input() resources: { title: string, url: string, message: string }[] = [];
  constructor(@Inject(MAT_DIALOG_DATA) public data: any,
   public dialogRef: MatDialogRef<ViewTransactionComponent>,
   private dialog: MatDialog,
   private store: Store<{ user: UserState }>,
   private httpRequestService: HttpRequestService) {}
   equipmentPrompts: string[] = [];
   newPrompt: string = '';
   isAdding: boolean = false;
  promptError: string = '';

  ngOnInit(): void {
    this.extractPdfLinks(this.data.response);
    this.fetchEquipmentData();


    if (this.data.response) {
      this.data.response = this.data.response.replace(/<br\s*\/?>/gi, '\n');
    }
  }

  extractPdfLinks(response: string): void {
    const regex = /<reference>.*?<document_name>(.*?)<\/document_name>.*?<page>(\d+)<\/page>.*?<\/reference>.*?(https?:\/\/[^\s]+)/gs;
    let match;
    let found = false;
    while ((match = regex.exec(response)) !== null) {
      found = true;
      let pdfTitle = match[1].split('/')[3];
      if (pdfTitle == undefined) {
        pdfTitle = match[1].split('/')[1];
      }
      this.resources.push({
        title: `${pdfTitle} (Page: ${match[2]})`,
        url: match[3],
        message: ''
      });
    }

    if (!found) {
      const urlRegex = /(https:\/\/[^\s]+\.pdf)/g;
      let urlMatch;
      while ((urlMatch = urlRegex.exec(response)) !== null) {
        const pdfTitle = urlMatch[1].substring(urlMatch[1].lastIndexOf('/') + 1);
        this.resources.push({
          title: pdfTitle,
          url: urlMatch[1],
          message: ''
        });
      }
    }
    this.data.response = this.data.response.replace(/<reference><document_name>(.*?)<\/document_name><page>(\d+)<\/page><\/reference>/gs, '$1 page $2\n');
    this.data.response = this.data.response.replace(/manuals\/[^/]+\/[^/]+\//g, '');
  }

  exportToPDF(): void {
    if (!this.data) {
      console.warn('No data available to export.');
      return;
    }

    const pdf = new jsPDF();
    pdf.setFontSize(16);
    pdf.text('Individual Query Record', 10, 20);

    pdf.setFontSize(12);
    let yPos = 30;
    const lineSpacing = 10;
    const pageHeight = pdf.internal.pageSize.getHeight();
    const bottomMargin = 20;
    const maxHeight = pageHeight - bottomMargin;

    const addText = (label: string, value: string) => {
      const text = `${label}: ${value || 'N/A'}`;
      const wrapped = pdf.splitTextToSize(text, 190);
      wrapped.forEach((line: string) => {
        if (yPos + lineSpacing > maxHeight) {
          pdf.addPage();
          yPos = 30;
        }
        pdf.text(line, 10, yPos);
        yPos += lineSpacing;
      });
    };
    addText('Equipment ID', this.data.equipment_code);
    addText('Make', this.data.make);
    addText('Model', this.data.model);
    addText('Serial No. Range', this.data.model_start_end?.split('#').slice(1).join(' - '));
    addText('PVC', this.data.pvc);
    addText('Region', this.data.region);
    addText('Submitted By', this.data.submittedBy);
    addText('Submitted At', this.data.start_timestamp);
    addText('Response Feedback', this.data.response_feedback);
    addText('Submission ID', this.data.user_id);

    if (this.equipmentPrompts?.length) {
      this.equipmentPrompts.forEach((prompt, index) => {
        addText(`Prompt ${index + 1}`, prompt);
      });
    } else {
      addText('Prompts', 'No prompts found for this equipment.');
    }
    addText('Question', this.data.user_input);

    const answerText = this.data.response?.replace(/<[^>]*>/g, '') || '';
    addText('Answer', answerText);

    if (this.resources?.length) {
      addText('Resources', '');
      this.resources.forEach((resource, index) => {
        addText(`  - Source ${index + 1}`, `${resource.title}: ${resource.url}`);
      });
    }
    pdf.save('individual-transaction.pdf');
  }

  closeModal(): void {
    this.dialogRef.close();
  }

  public openPDFModal(pdfUrl: string): void {
    const fileName = this.extractPdfName(pdfUrl) + '.pdf';
    const manualType = pdfUrl.split('/').slice(-2, -1)[0];
    const documentMake = pdfUrl.split('/').slice(-3, -2)[0];
    this.downloadURL = `${environment.integrations.manualDownload}?equipment_make=${documentMake}&file_name=${encodeURIComponent(fileName)}&manual_type=${manualType}`;
    if (this.downloadURL) {
      this.fetchDownloadUrl(this.downloadURL).then(resource => {
        this.dialog.open(ModalPdfComponent, {
          data: { pdfUrl: resource.downloadUrl }
        });
      }).catch(error => {
        console.error('Error fetching download URL:', error);
      });
    } else {
      console.error('Uploaded URL is null.');
    }
  }

  private fetchDownloadUrl(providerResourceUrl: string): Promise<{ downloadUrl: string, s3Key: string }> {
    return new Promise((resolve, reject) => {
      this.httpRequestService.request<any>('GET', providerResourceUrl).subscribe({
        next: (response) => resolve({ downloadUrl: response.download_url, s3Key: response.s3_key }),
        error: (error) => reject(error)
      });
    });
  }

  extractPdfName(url: string): string {
    const match = url.match(/\/([^/]+)\.pdf$/);
    return match ? match[1] : '';
  }

  private fetchEquipmentData(): void {
    const url = environment.integrations.prompts;
    const make = this.data?.make;
    const model = this.data?.model;
    const params = `make=${make}&model=${model || ''}`;

    this.httpRequestService.request<any>('GET', `${url}?${params}`).subscribe({
      next: (response) => {
        const equipment = response;
        if (equipment?.PROMPTS?.resources && Array.isArray(equipment.PROMPTS.resources)) {
          this.equipmentPrompts = equipment.PROMPTS.resources.map((resource: any) => resource.prompt) || [];
        } else {
          this.equipmentPrompts = [];
        }
      },
      error: (error) => {
        console.error('Failed to fetch equipment data:', error);
        this.equipmentPrompts = [];
      }
    });
  }

  addPrompt(): void {
    if (this.equipmentPrompts.length < this.maxPrompts){
      this.isAdding = true;
      this.newPrompt = '';
    }
    else{
      this.promptError = `You\'ve reached the limit of ${this.maxPrompts} active prompts for this equipment model. To add a new one, please remove an existing prompt first.`
    } 
  }
  
  async saveNewPrompt(): Promise<void> {
    this.isAdding = false;
    const trimmedPrompt = this.newPrompt?.trim();
  
    if (!trimmedPrompt) {
      alert('Prompt cannot be empty.');
      return;
    }
  
    const isDuplicate = this.equipmentPrompts.some(
      existing => existing.toLowerCase() === trimmedPrompt.toLowerCase()
    );
  
    if (isDuplicate) {
      alert('This prompt already exists. Please try another one.');
      return;
    }

    const userState = await lastValueFrom(this.store.select('user').pipe(take(1)));
    const email = userState.userInfo?.email;
    const uploaded_by = email ? email.split('@')[0] : 'anonymous';
    this.httpRequestService.request<any>('PUT', `${environment.integrations.addCommonPrompt}`, {
      make: this.data.make,
      model: this.data.model,
      common_prompt: trimmedPrompt,
      uploaded_by: uploaded_by,
    }).subscribe({
      next: () => {
        this.equipmentPrompts.push(trimmedPrompt);
        this.newPrompt = '';
      },
      error: (error) => {
        console.error('Error saving prompts:', error);
      }
    });
  }
  
  removePrompt(index: number): void {
    const promptToRemove = this.equipmentPrompts[index];

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      data: {
        message: 'Are you sure you want to remove the prompt?',
        prompt: promptToRemove,
      },
    });

    dialogRef.afterClosed().subscribe(async (confirmed: boolean) => {
      if (!confirmed) return;
      try {
        await Promise.all(this.equipmentPrompts.map(item =>
          lastValueFrom(
            this.httpRequestService
              .request<any>('DELETE', `${environment.integrations.deleteCommonPrompt}`, {
                make: this.data.make,
                model: this.data.model,
                common_prompt: promptToRemove,
              })
              .pipe(take(1))
          ))
        );
        this.equipmentPrompts.splice(index, 1);
      } catch (error) {
        console.error('Error deleting prompts:', error);
        alert('Failed to delete prompt. Please try again.');
      }
    });
  }

  formatAnswerWithBreaks(response: string): string {
    let formatted = response.replace(/(?<!\.\w{2,5})\s(\d+\.)/g, '<br/><br/>$1');

    formatted = formatted.replace(/(REFERENCES:)/g, '<br/><br/>$1');
    formatted = formatted.replace(/\n/g, '<br/>');

    return formatted.trim();
  }
  
}